import { NoInfer } from 'react-redux';

import { ActionReducerMapBuilder, Draft, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  FulfilledActionType,
  PendingActionType,
  RejectedActionType,
  errorOr,
  fulfilled,
  pending,
  rejected
} from 'store/storeHelpers';

import { ArkivResursData, GranskningResurs, ModelError, State, VinjettData } from 'models';
import { HTTP_STATUS } from 'models/appModels/Request';

import BebyggelseApi from 'api/bebyggelseApi';

export const fetchMellanlagradResurs = createAsyncThunk<GranskningResurs, string, { rejectValue: ModelError }>(
  'get/vinjett/mellanlagrad',
  async (resursPath: string, { rejectWithValue }): Promise<any> => {
    const result = await BebyggelseApi.vinjett.getGranskningResurs(resursPath);
    return errorOr(result, rejectWithValue, [HTTP_STATUS.NOT_FOUND]);
  }
);

export const fetchArkiveradResurs = createAsyncThunk<ArkivResursData, string, { rejectValue: ModelError }>(
  'get/vinjett/arkiverad',
  async (resursUrl: string, { rejectWithValue }): Promise<any> => {
    const result = await fetch(resursUrl);
    return errorOr(result, rejectWithValue, [HTTP_STATUS.NOT_FOUND]);
  }
);

const initialState: State<VinjettData> = {
  requestStatus: 'idle',
  requestType: undefined,
  data: undefined,
  currentRequestId: undefined,
  error: undefined
};

const vinjettSlice = createSlice({
  name: 'vinjett',
  initialState,
  reducers: {
    clearVinjett: (state: Draft<State<VinjettData>>) => {
      state.data = undefined;
    }
  },
  extraReducers: (builder: ActionReducerMapBuilder<NoInfer<State<VinjettData>>>): void => {
    //FETCH mellanlagrad
    builder.addCase(
      fetchMellanlagradResurs.pending,
      (state: Draft<State<VinjettData>>, action: PendingActionType): void => pending(state, action)
    );
    builder.addCase(
      fetchMellanlagradResurs.fulfilled,
      (state: Draft<State<VinjettData>>, action: FulfilledActionType<GranskningResurs>): void => {
        const vinjettData = {
          thumbnailUrl: `${process.env.PUBLIC_URL}/api${action.payload.thumbnail_url}`,
          url: `${process.env.PUBLIC_URL}/api${action.payload.url}`,
          titel: action.payload.resurs_metadata?.titel
        } as VinjettData;
        const newAction: FulfilledActionType<VinjettData> = {
          payload: vinjettData,
          meta: action.meta,
          type: action.type
        };
        fulfilled(state, newAction);
      }
    );
    builder.addCase(
      fetchMellanlagradResurs.rejected,
      (state: Draft<State<VinjettData>>, action: RejectedActionType): void => {
        state.data = undefined;
        rejected(state, action);
      }
    );

    //FETCH arkiverad
    builder.addCase(fetchArkiveradResurs.pending, (state: Draft<State<VinjettData>>, action: PendingActionType): void =>
      pending(state, action)
    );
    builder.addCase(
      fetchArkiveradResurs.fulfilled,
      (state: Draft<State<VinjettData>>, action: FulfilledActionType<ArkivResursData>): void => {
        const vinjettData =
          action.payload?.resourceGroups?.length > 0
            ? ({
                thumbnailUrl: action.payload.resourceGroups[0]?.presentationThumbnailUrl,
                url: action.payload.resourceGroups[0].resources[0]?.uri,
                titel: action.payload.attributes.find((att) => att.name === 'titel')?.value[0]
              } as VinjettData)
            : undefined;
        const newAction: FulfilledActionType<VinjettData | undefined> = {
          payload: vinjettData,
          meta: action.meta,
          type: action.type
        };
        fulfilled(state, newAction);
      }
    );
    builder.addCase(
      fetchArkiveradResurs.rejected,
      (state: Draft<State<VinjettData>>, action: RejectedActionType): void => {
        state.data = undefined;
        rejected(state, action);
      }
    );
  }
});

export const { clearVinjett } = vinjettSlice.actions;
export default vinjettSlice.reducer;
